/**
 * Unified power-up shop.
 *
 * Surfaces both eraser and 50/50 bundles in one place so a user landing
 * here doesn't have to remember which power-up has its own page. Pricing,
 * the bonus-first split, and the per-purchase idempotency key all mirror
 * the dedicated pages — this page is a routing/UX convenience, not a new
 * money flow.
 *
 * Visual structure:
 *   - Single hero card with inventory counts for both power-ups.
 *   - Shared wallet strip (cash + bonus) with the "spends bonus first"
 *     note. Rendered once at the top so it doesn't repeat per section.
 *   - "Erasers" section — gold-themed bundle cards.
 *   - "50/50s" section — blue-themed bundle cards.
 *   - Footer note pointing at the earn paths (referrals + rank rewards)
 *     so we don't make the shop look like the only acquisition route.
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { formatKobo } from '../wallet.js';

const ERASER_ACCENT = '#E5C100';
const ERASER_BG = 'rgba(229,193,0,0.10)';
const ERASER_BORDER = 'rgba(229,193,0,0.40)';

const FIFTY_ACCENT = '#7FB4FF';
const FIFTY_BG = 'rgba(127,180,255,0.12)';
const FIFTY_BORDER = 'rgba(127,180,255,0.42)';

const ERASER_SVG = `
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M20.71 14.29l-7-7a1 1 0 0 0-1.42 0L3.71 15.88a1 1 0 0 0 0 1.41l4.79 4.79a1 1 0 0 0 1.41 0L20.71 11.7a1 1 0 0 0 0-1.41z"></path>
    <path d="M14 22H7"></path>
  </svg>
`;

const FIFTY_SVG = `
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9"></circle>
    <line x1="12" y1="3" x2="12" y2="21"></line>
    <path d="M12 3a9 9 0 0 0 0 18z" fill="currentColor" stroke="none"></path>
  </svg>
`;

export async function renderShop(root) {
  root.innerHTML = `
    <div class="appbar">
      <a href="/wallet" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Shop</div>
    </div>
    <div class="pad" id="shop-body">
      <div class="skel"></div>
      <div class="skel"></div>
      <div class="skel"></div>
    </div>
  `;

  const host = document.getElementById('shop-body');
  let eraserBal;
  let eraserProducts;
  let fiftyBal;
  let fiftyProducts;
  let wallet;
  try {
    // One fan-out: balances, both catalogs, wallet. Parallel so the
    // page paints in one round-trip instead of stepping through
    // sequentially.
    const [eb, ep, fb, fp, w] = await Promise.all([
      api('/erasers/balance'),
      api('/erasers/products'),
      api('/fiftyfifty/balance'),
      api('/fiftyfifty/products'),
      api('/wallet'),
    ]);
    eraserBal = eb.eraser_balance;
    eraserProducts = ep.items;
    fiftyBal = fb.fiftyfifty_balance;
    fiftyProducts = fp.items;
    wallet = w;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    toast.fromError(err);
    host.innerHTML = `<div class="card" style="color:var(--pink-t)">${escape(err.message)}</div>`;
    return;
  }

  host.innerHTML = `
    ${heroHtml(eraserBal, fiftyBal)}
    ${walletStripHtml(wallet)}
    ${sectionHtml({
      title: 'Erasers',
      products: eraserProducts,
      kind: 'eraser',
      accent: ERASER_ACCENT,
      bg: ERASER_BG,
      border: ERASER_BORDER,
      iconSvg: ERASER_SVG,
      iconBg: '#3a2a14',
      explainer: 'Auto-corrects your first wrong answer in a game.',
    })}
    ${sectionHtml({
      title: '50/50s',
      products: fiftyProducts,
      kind: 'fiftyfifty',
      accent: FIFTY_ACCENT,
      bg: FIFTY_BG,
      border: FIFTY_BORDER,
      iconSvg: FIFTY_SVG,
      iconBg: '#15233F',
      explainer: 'Removes two wrong answers in a live game. Use up to 2 per game.',
    })}

    <div class="muted" style="font-size:11.5px;line-height:1.5;margin-top:16px;text-align:left;">
      Paid from your wallet · You can also win power-ups by finishing in
      a live game's reward positions, or
      <a href="/invite" data-link style="color:${FIFTY_ACCENT};">earn 50/50s by referring friends</a>.
    </div>
  `;

  document.querySelectorAll('[data-buy-kind]').forEach((btn) => {
    btn.addEventListener('click', () =>
      buy(btn.dataset.buyKind, btn.dataset.buy),
    );
  });
}

function heroHtml(eraserBal, fiftyBal) {
  // Side-by-side inventory tiles. Smaller than the per-product hero on
  // /erasers + /fiftyfifty because the shop is a hub — we don't want
  // either glyph to dominate.
  return `
    <div class="card" style="padding:16px;background:radial-gradient(120% 100% at 50% 0%, rgba(91,44,145,0.30) 0%, rgba(21,16,43,0.85) 60%);border:1px solid var(--border);">
      <div style="font-size:11px;letter-spacing:0.18em;text-transform:uppercase;color:rgba(255,255,255,0.65);font-weight:700;margin-bottom:8px;text-align:center;">
        Your power-ups
      </div>
      <div class="row" style="gap:10px;align-items:stretch;">
        ${heroTileHtml({
          label: 'Erasers',
          balance: eraserBal,
          accent: ERASER_ACCENT,
          bg: ERASER_BG,
          border: ERASER_BORDER,
          icon: ERASER_SVG,
          id: 'shop-eraser-balance',
        })}
        ${heroTileHtml({
          label: '50/50s',
          balance: fiftyBal,
          accent: FIFTY_ACCENT,
          bg: FIFTY_BG,
          border: FIFTY_BORDER,
          icon: FIFTY_SVG,
          id: 'shop-fiftyfifty-balance',
        })}
      </div>
    </div>
  `;
}

function heroTileHtml({ label, balance, accent, bg, border, icon, id }) {
  return `
    <div class="grow" style="background:${bg};border:1px solid ${border};border-radius:12px;padding:12px;text-align:center;color:${accent};">
      <div style="display:inline-flex;align-items:center;justify-content:center;width:34px;height:34px;color:${accent};">
        ${icon}
      </div>
      <div style="font-size:10.5px;letter-spacing:0.10em;text-transform:uppercase;font-weight:700;margin-top:4px;color:${accent};">
        ${escape(label)}
      </div>
      <div id="${id}" style="font-family:var(--font-display);font-size:30px;font-weight:800;color:${accent};line-height:1.05;margin-top:2px;">
        ${balance}
      </div>
    </div>
  `;
}

function walletStripHtml(wallet) {
  // Render the static markup with ids the buy flow can update in place.
  return `
    <div id="shop-wallet-strip" class="card" style="padding:12px 14px;margin-top:10px;">
      <div class="row" style="gap:12px;align-items:center;">
        <div class="grow" style="min-width:0;">
          <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;">Wallet</div>
          <div style="font-family:var(--font-display);font-size:18px;color:#fff;font-weight:700;line-height:1.15;margin-top:2px;" id="shop-wallet-cash">
            ${escape(formatKobo(wallet.balance_kobo))}
          </div>
        </div>
        <div style="text-align:right;">
          <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:#E94BA2;font-weight:700;">🎁 Bonus</div>
          <div style="font-family:var(--font-display);font-size:18px;color:#fff;font-weight:700;line-height:1.15;margin-top:2px;" id="shop-wallet-bonus-amount">
            ${escape(formatKobo(Number(wallet.bonus_balance_kobo || 0)))}
          </div>
        </div>
      </div>
      <div class="muted" style="font-size:11px;line-height:1.45;margin-top:8px;">
        Purchases spend bonus first, then cash. Win a 1-on-1 duel to convert bonus to cash.
      </div>
    </div>
  `;
}

function sectionHtml({ title, products, kind, accent, bg, border, iconSvg, iconBg, explainer }) {
  const popularId = products
    .filter((p) => p.count > 1)
    .reduce(
      (best, p) => (best == null || p.unit_price_kobo < best.unit_price_kobo ? p : best),
      null,
    )?.id;
  const single = products.find((p) => p.count === 1);
  const singleUnit = single ? single.price_kobo : null;

  const cards = products
    .map((p) =>
      productCardHtml(p, {
        isPopular: p.id === popularId,
        singleUnit,
        kind,
        accent,
        border,
        iconSvg,
        iconBg,
      }),
    )
    .join('');

  return `
    <h3 class="sec" style="margin-top:18px;color:${accent};">${escape(title)}</h3>
    <div class="muted" style="font-size:11.5px;line-height:1.45;margin-top:-2px;margin-bottom:10px;">
      ${escape(explainer)}
    </div>
    <div style="display:grid;gap:10px;">
      ${cards}
    </div>
  `;
}

function productCardHtml(p, { isPopular, singleUnit, kind, accent, border, iconSvg, iconBg }) {
  const labelN =
    kind === 'eraser'
      ? p.count === 1 ? '1 Eraser' : `${p.count} Erasers`
      : p.count === 1 ? '1 × 50/50' : `${p.count} × 50/50`;
  let savingsKobo = 0;
  if (singleUnit && p.count > 1) {
    savingsKobo = Math.max(0, singleUnit * p.count - p.price_kobo);
  }
  const savingsLine =
    savingsKobo > 0
      ? `<div style="font-size:11.5px;font-weight:700;color:#2BB39B;margin-top:2px;letter-spacing:0.03em;">SAVE ${escape(formatKobo(savingsKobo))}</div>`
      : '';

  const popularBg = kind === 'eraser' ? '#2A2244' : '#1B2640';
  const popularBorder = kind === 'eraser' ? '#3a2f5e' : '#2c3d62';
  const popularBadge = isPopular
    ? `<span style="display:inline-flex;align-items:center;padding:4px 9px;background:${popularBg};border:1px solid ${popularBorder};border-radius:6px;font-size:10px;font-weight:800;color:${accent};letter-spacing:0.10em;font-family:var(--font-display);text-transform:uppercase;margin-right:6px;">
         Popular
       </span>`
    : '';

  const cardStyle = isPopular
    ? `padding:14px;background:var(--surf-2);border:1.5px solid ${border};box-shadow:0 0 0 1px ${border};`
    : 'padding:14px;background:var(--surf-2);';

  // The 50/50 buy button uses a custom blue button (matching the
  // dedicated /fiftyfifty page); the eraser one uses the existing
  // `btn sm gold` style. Keeps prices visually anchored to their
  // power-up's accent.
  const buyBtn =
    kind === 'eraser'
      ? `<button type="button" class="btn sm gold" data-buy-kind="${escape(kind)}" data-buy="${escape(p.id)}"
                style="white-space:nowrap;min-width:88px;text-align:center;font-weight:800;">
          ${escape(formatKobo(p.price_kobo))}
        </button>`
      : `<button type="button" class="btn sm" data-buy-kind="${escape(kind)}" data-buy="${escape(p.id)}"
                style="white-space:nowrap;min-width:88px;text-align:center;font-weight:800;background:${accent};color:#0A1830;border-bottom-color:#3A6BA3;">
          ${escape(formatKobo(p.price_kobo))}
        </button>`;

  return `
    <div class="card" style="${cardStyle}">
      <div class="row" style="align-items:center;gap:12px;">
        <div style="width:42px;height:42px;flex-shrink:0;background:${iconBg};border:1px solid ${border};border-radius:10px;display:flex;align-items:center;justify-content:center;color:${accent};">
          ${iconSvg}
        </div>
        <div class="grow" style="min-width:0;">
          <div style="font-size:16px;color:#fff;font-weight:700;">${escape(labelN)}</div>
          ${savingsLine}
        </div>
        ${popularBadge}
        ${buyBtn}
      </div>
    </div>
  `;
}

async function buy(kind, productId) {
  const btn = document.querySelector(
    `[data-buy-kind="${CSS.escape(kind)}"][data-buy="${CSS.escape(productId)}"]`,
  );
  if (!btn) return;
  const confirmMsg =
    kind === 'eraser'
      ? 'Purchase erasers? Your wallet will be charged.'
      : 'Purchase 50/50s? Your wallet will be charged.';
  if (!confirm(confirmMsg)) return;

  btn.disabled = true;
  const original = btn.textContent;
  btn.textContent = 'Buying…';
  try {
    const endpoint = kind === 'eraser' ? '/erasers/purchase' : '/fiftyfifty/purchase';
    const idempotency_key = `${kind === 'eraser' ? 'er' : 'ff'}-buy-${productId}-${crypto.randomUUID()}`;
    const res = await api(endpoint, {
      method: 'POST',
      body: { product_id: productId, idempotency_key },
    });
    // Update the relevant hero tile + cached me.
    if (kind === 'eraser') {
      document.getElementById('shop-eraser-balance').textContent = res.eraser_balance;
      const me = auth.getMe();
      if (me) auth.setMe({ ...me, eraser_balance: res.eraser_balance });
      toast.success(`Got ${res.count} eraser${res.count === 1 ? '' : 's'}!`);
    } else {
      document.getElementById('shop-fiftyfifty-balance').textContent = res.fiftyfifty_balance;
      const me = auth.getMe();
      if (me) auth.setMe({ ...me, fiftyfifty_balance: res.fiftyfifty_balance });
      toast.success(`Got ${res.count} × 50/50!`);
    }
    // Refresh wallet strip so bonus-first drain is visible immediately.
    try {
      const w = await api('/wallet');
      const cash = document.getElementById('shop-wallet-cash');
      const bonus = document.getElementById('shop-wallet-bonus-amount');
      if (cash) cash.textContent = formatKobo(w.balance_kobo);
      if (bonus) bonus.textContent = formatKobo(Number(w.bonus_balance_kobo || 0));
    } catch {
      // Soft-fail: the purchase succeeded; a stale strip is cosmetic.
    }
  } catch (err) {
    toast.fromError(err);
  } finally {
    btn.disabled = false;
    btn.textContent = original;
  }
}
